//@flow
import { setAlertData, stopLoading } from "Redux/Screen/Action";
import axios from "axios";
import { store } from "Redux/Store";
import {
  CheckPaginateNumber,
  CheckStringEmpty,
  Logout,
  getPaginationPage,
} from "Utils/common";
export const authTokenKey = "Authorization";

/**
 * Where a forced-logout reason is parked so it survives the redirect.
 *
 * Logout() navigates with window.location, which tears down the React app and
 * the Redux store, so an alert dispatched at the point of failure never gets
 * rendered. sessionStorage is used rather than localStorage because Logout()
 * and the login screen both call localStorage.clear().
 */
export const LOGOUT_REASON_KEY = "cm-logout-reason";

const apiBaseUrls = {
  prod: process.env.REACT_APP_BASE_URL,
  dev: process.env.REACT_APP_BASE_URL,
  stage: process.env.REACT_APP_BASE_URL,
}; /*  */

const apiBaseUrlsAuth = {
  prod: process.env.REACT_APP_BASE_AUTH_URL,
  dev: process.env.REACT_APP_BASE_AUTH_URL,
  stage: process.env.REACT_APP_BASE_AUTH_URL,
};

const apiBaseUrlsRedox = {
  prod: process.env.REACT_APP_BASE_REDOX_URL,
  dev: process.env.REACT_APP_BASE_REDOX_URL,
  stage: process.env.REACT_APP_BASE_REDOX_URL,
};

const apiBaseUrlsEhr = {
  prod: process.env.REACT_APP_BASE_EHR_URL,
  dev: process.env.REACT_APP_BASE_EHR_URL,
  stage: process.env.REACT_APP_BASE_EHR_URL,
};

const react_app_env = {
  prod: "production",
  dev: "development",
};

let baseUrl = apiBaseUrls.dev;
if (process.env.NODE_ENV === react_app_env.prod) {
  baseUrl = apiBaseUrls.prod;
}

let baseUrlAuth = apiBaseUrlsAuth.dev;
if (process.env.NODE_ENV === react_app_env.prod) {
  baseUrlAuth = apiBaseUrlsAuth.prod;
}

let baseUrlRedox = apiBaseUrlsRedox.dev;
if (process.env.NODE_ENV === react_app_env.prod) {
  baseUrlRedox = apiBaseUrlsRedox.prod;
}

let baseUrlEhr = apiBaseUrlsEhr.dev;
if (process.env.NODE_ENV === react_app_env.prod) {
  baseUrlEhr = apiBaseUrlsEhr.prod;
}

export const get = (request: any) => {
  return commonFetch({ method: "get", ...request });
};

export const post = (request: any) => {
  return commonFetch({ method: "post", ...request });
};

export const patch = (request: any) => {
  return commonFetch({ method: "patch", ...request });
};

export const put = (request: any) => {
  return commonFetch({ method: "put", ...request });
};

export const deletee = (request: any) => {
  return commonFetch({ method: "delete", ...request });
};

const inFlightRequests = new Map<string, AbortController>();

const commonFetch = (request: any) => {
  const dispatch = store.dispatch;
  try {
    const {
      subUrl,
      method,
      data = {},
      params = {},
      headers = {},
      isAuth,
      isRedox,
      isEhr,
      excel,
      dedupe,
      dedupeKey,
      acceptRaw,
      silentStatuses,
    } = request;

    let url;
    if (isAuth) url = `${baseUrlAuth}/auth/${subUrl}`;
    else if (isRedox) url = `${baseUrl}/redox/${subUrl}`;
    // Vendor-aware sync callers provide the complete ehr-athena/**, ehr-ecw/**
    // or vendor-neutral ehr-api/ehr/** route. Keep the Athena prefix for older EHR
    // settings/push calls until those operations gain eCW equivalents.
    else if (isEhr) {
      const ehrSubUrl = /^(ehr-(athena|ecw)|ehr-api\/ehr)\//.test(subUrl)
        ? subUrl
        : `ehr-athena/${subUrl}`;
      url = `${baseUrlEhr}/${ehrSubUrl}`;
    }
    else url = `${baseUrl}/api/${subUrl}`;

    const passedHeaders = headers; 
    const commonHeaders = getCommonHeaders(excel);
    const responseType = excel ? "blob" : "json";

    const key = dedupeKey || (dedupe ? subUrl : undefined);
    let controller: AbortController | undefined;
    if (key) {
      inFlightRequests.get(key)?.abort();
      controller = new AbortController();
      inFlightRequests.set(key, controller);
    }
    const clearInFlight = () => {
      if (key && inFlightRequests.get(key) === controller) {
        inFlightRequests.delete(key);
      }
    };

    return axios({
      method,
      url,
      // data: { data },
      responseType,
      data,
      withCredentials: false,
      headers: excel
        ? { ...commonHeaders }
        : { ...commonHeaders, ...passedHeaders },
      params,
      signal: controller?.signal,
    })
      .then((response) => {
        clearInFlight();
        if (acceptRaw && response.status >= 200 && response.status < 300) {
          return { success: true, data: response.data };
        } else if (response?.data?.status == 1) {
          return {
            success: true,
            data: response?.data?.data,
            criterion: {
              limit: CheckPaginateNumber(response?.data?.criterion?.limit),
              page: getPaginationPage(
                CheckPaginateNumber(response?.data?.criterion?.offset),
                CheckPaginateNumber(response?.data?.criterion?.limit)
              ),
              total: CheckPaginateNumber(response?.data?.criterion?.total),
            },
            alert: {
              alertMessage: CheckStringEmpty(
                response?.data?.msg || response?.data?.error || "Save Changes"
              ),
              openAlert: true,
              alertVariant: "success",
            },
            msg: response?.data?.msg,
            error: response?.data?.error,
            status: response?.data?.status,
          };
        } else if (excel) {
          return { success: true, data: response?.data };
        } else if (response?.data?.status == 2) {
          const alert = {
            alertMessage: CheckStringEmpty(
              response?.data?.error ||
              response?.data?.msg ||
              "Something went wrong!"
            ),
            openAlert: true,
            alertVariant: "warning" as const,
          };
          dispatch(
            setAlertData(alert)
          );
          dispatch(stopLoading());
          return {
            success: false,
            data: response?.data?.data,
            alert,
            msg: response?.data?.msg,
            error: response?.data?.error,
            status: response?.data?.status,
          };
        }
        else {
          const alert = {
            alertMessage: CheckStringEmpty(
              response?.data?.error ||
              response?.data?.msg ||
              "Something went wrong!"
            ),
            openAlert: true,
            alertVariant: "error" as const,
          };
          dispatch(
            setAlertData(alert)
          );
          dispatch(stopLoading());
          return {
            success: false,
            data: response?.data?.data,
            alert,
            msg: response?.data?.msg,
            error: response?.data?.error,
            status: response?.data?.status,
          };
        }
      })
      .catch((error) => {
        clearInFlight();
        if (axios.isCancel(error)) {
          return {
            success: false,
            canceled: true,
          };
        }
        // A cancelled request is not a failed one. Deduping aborts the request already in
        // flight whenever the same one is asked for again -- pressing an EHR Sync button twice
        // does exactly that -- and an aborted axios error carries no `response`, so every status
        // branch below misses and it fell through to the catch-all, which logs the user out.
        // That is CV-435: syncing with the EHR more than once signed you out.
        //
        // Nothing is reported either. The caller that superseded this request is still running
        // and will report for itself; an alert here would describe a request the user replaced on
        // purpose.
        if (axios.isCancel(error) || error?.code === "ERR_CANCELED") {
          dispatch(stopLoading());
          return { success: false, cancelled: true };
        }
        const apiErrMsg =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.response?.data?.msg;
        // A probe that asks "may I?" receives its answer as a status, not as a failure worth
        // showing the user. Callers opt in per request, so nothing is silenced by default and
        // every existing call keeps its alert.
        //
        // 401 is never silenced, whatever a caller passes: an expired session must still reach
        // Logout() below rather than being swallowed into a quiet empty result.
        const responseStatus = error?.response?.status;
        if (
          responseStatus != 401 &&
          Array.isArray(silentStatuses) &&
          silentStatuses.includes(responseStatus)
        ) {
          dispatch(stopLoading());
          return { success: false, status: responseStatus };
        }
        if (error?.response?.status == 400) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "Information can't be processed",
              openAlert: true,
              alertVariant: "error",
            })
          );
        } else if (error?.response?.status == 401) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "Session has expired. Please log in again.",
              openAlert: true,
              alertVariant: "error",
            })
          );
          Logout();
        } else if (error?.response?.status == 403) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "You are not authorized to perform this action.",
              openAlert: true,
              alertVariant: "error",
            })
          );
        } else if (error?.response?.status == 404) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "Resource not found. Please try after sometime.",
              openAlert: true,
              alertVariant: "error",
            })
          );
        } else if (error?.response?.status == 409) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "User login to another system with these credentials.",
              openAlert: true,
              alertVariant: "error",
            })
          );
          try {
            sessionStorage.setItem(
              LOGOUT_REASON_KEY,
              apiErrMsg ||
              "User login to another system with these credentials."
            );
          } catch {
            /* sessionStorage unavailable — fall back to the transient alert */
          }
          Logout();
        } else if (error?.response?.status == 500) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "Something went wrong, please try again later.",
              openAlert: true,
              alertVariant: "error",
            })
          );
        } else if (error?.response?.status == 503) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "We are experiencing heavy server loads, some feature might be slow",
              openAlert: true,
              alertVariant: "error",
            })
          );
        } else if (
          error?.response?.status == 408 ||
          error?.response?.status == 504 ||
          error?.code === "ECONNABORTED" ||
          /timeout/i.test(error?.message || "")
        ) {
          dispatch(
            setAlertData({
              alertMessage:
                apiErrMsg ||
                "Request timed out. Please try again after some time.",
              openAlert: true,
              alertVariant: "error",
            })
          );
        } else {
          dispatch(
            setAlertData({
              alertMessage:
                "Something went wrong. Please login after sometime.",
              openAlert: true,
              alertVariant: "error",
            })
          );
        }
        dispatch(stopLoading());
        return {
          success: false,
        };
      });
  } catch (err) {
    dispatch(
      setAlertData({
        openAlert: true,
        alertMessage: JSON.stringify(err),
        alertVariant: "error",
      })
    );
    dispatch(stopLoading());
    return {
      success: false,
    };
  }
};

const getCommonHeaders = (excel?: boolean) => {
  const localToken = localStorage.getItem("cm-token") || "";
  const tokenStorage = localToken ? JSON.parse(localToken) : "";

  const headerObj: any = {
    "Content-Type":
      // excel ? "application/vnd.ms-excel" :
      "application/json",
    // Accept: excel
    //   ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    //   : "",
    "Access-Control-Allow-Origin": true,
    [authTokenKey]: tokenStorage ? `Bearer ${tokenStorage}` : "",
  };
  const headerObjForExcel: any = {
    [authTokenKey]: tokenStorage ? `Bearer ${tokenStorage}` : "",
  };
  return excel ? headerObjForExcel : headerObj;
};
